import { useEffect, useRef, useState } from 'react';
import { withStyles, CardTabs } from 'vopay';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { Title, Button, UtilButton } from 'UI';
import styles from './ClientAccountPage.module.scss';
import CustomerOverview from '@/components/Customer/Overview/CustomerOverview';
import BalancesCard from './BalancesCard';
import TransactionLimitCard from './TransactionLimitCard';
import DetailsTab from '@/components/Customer/Tabs/Details/DetailsTab';
import SettingsTab from '@/components/Customer/Tabs/Settings/SettingsTab';
import PaymentMethodsTab from '@/components/Customer/Tabs/PaymentMethods/PaymentMethodsTab';
import ActivityTab from '@/components/Customer/Tabs/Activity/ActivityTab';
import CportalTab from '@/components/Customer/Tabs/Cportal/CportalTab';
import useClientAccounts from '@/hooks/useApi/useClientAccounts';
import { usePermissions } from '@/providers/Permissions.provider';
import { useTranslation } from 'react-i18next';
import { useClientData } from '@/providers/ClientData.provider';
import { useNavMenu } from '@/providers/NavMenu.provider';
import usePaymentMethods from '@/hooks/usePaymentMethods';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useDialog } from '@/providers/Dialog.provider';
import useCportal from '@/hooks/useApi/useCportal';
import LinkDialog from './LinkDialog';

const cn = withStyles(styles);

const ClientAccountPage = () => {
  const location = useLocation<{ showLinkDialog?: boolean }>();
  const history = useHistory();
  const { accountID } = useParams<{ accountID: string }>();
  const { clientAccount, fetchClientAccount } = useClientAccounts();
  const [tab, setTab] = useState('details');

  const { permissions } = usePermissions();
  const { t } = useTranslation('translations', { keyPrefix: 'ClientAccount' });
  const { t: tCustomer } = useTranslation('translations', { keyPrefix: 'Customers' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const {
    response: { Country, CPortalEnabled },
  } = useClientData();
  const { setSubNavActiveModule } = useNavMenu();
  const alertDialog = useAlertDialog();
  const dialog = useDialog();
  const { services, cPortalLink, fetchAccountCportalSettings } = useCportal();

  const { groupedMethods, fetchMethods, setDefaultMethod, deleteMethod, deleteGroupedMethod } = usePaymentMethods({
    accountID,
    currencies: ['CAD'],
  });

  const showLinkDialogRef = useRef(location.state?.showLinkDialog ?? false);

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const newTab = search.get('cardTab');
    newTab && setTab(newTab);
  }, [location.search]);

  useEffect(() => {
    fetchClientAccount(accountID);
    fetchAccountCportalSettings();
    setSubNavActiveModule('clientAccountsList');
    refreshPaymentMethods();
  }, []);

  async function handleLinkDialog(link: string) {
    await dialog.open(({ onCancel }) => <LinkDialog link={link} onClose={onCancel} />);
  }

  useEffect(() => {
    if (cPortalLink && showLinkDialogRef.current) {
      showLinkDialogRef.current = false;
      history.replace(location.pathname + location.search, {});
      handleLinkDialog(cPortalLink);
    }
  }, [cPortalLink]);

  if (!clientAccount) return null;

  async function handleUpdate() {
    return fetchClientAccount(accountID);
  }

  async function handleCportalSettingsUpdate(enabled = false) {
    handleUpdate();
    await fetchAccountCportalSettings();
    if (enabled) handleLinkDialog(cPortalLink);
  }

  const accountName = clientAccount.ClientName;

  async function refreshPaymentMethods() {
    try {
      const res = await fetchMethods();

      return res;
    } catch (err) {
      const msg = err instanceof Error ? err.message : `Couldn't fetch payment methods for ${accountID}`;
      alertDialog.error(tGlobal('error'), msg);
    }
  }

  return (
    <div className={cn('clientAccountPage')}>
      <UtilButton
        iconLeft="chevron_left"
        size="large"
        onClick={() => {
          if (history.length > 1) {
            history.goBack();
          } else {
            history.replace('/client-accounts');
          }
        }}
      >
        {history.length > 1 ? tGlobal('Buttons.back') : t('clientAccounts', { defaultValue: 'Client Accounts' })}
      </UtilButton>

      <header className={cn('pageHeader')}>
        <Title>{t('pageTitle', { defaultValue: 'Client Account Details' })}</Title>

        <div className={cn('pageHeaderLinks')}>
          {permissions.CreateClientAccountTransfers && (
            <Button
              onClick={() => {
                history.push('/client-account-transfer-funds');
              }}
              disabled={!clientAccount.IsActive}
            >
              {t('transferFunds', { defaultValue: 'Transfer Funds' })}
            </Button>
          )}

          {permissions.CreatePayments && (
            <Button
              onClick={() => {
                history.push('/single-payment', {
                  contact: clientAccount.ClientAccountID,
                });
              }}
              disabled={!clientAccount.IsActive}
            >
              {tCustomer('payment', {
                defaultValue: 'Payment',
              })}
            </Button>
          )}

          {permissions.CreateCollections && (
            <Button
              onClick={() => {
                history.push('/single-collection', {
                  contact: clientAccount.ClientAccountID,
                });
              }}
              disabled={!clientAccount.IsActive}
            >
              {tCustomer('collection', {
                defaultValue: 'Collection',
              })}
            </Button>
          )}
        </div>
      </header>

      <div className={cn('overview')}>
        <CustomerOverview
          accountID={clientAccount.ClientAccountID}
          active={clientAccount.IsActive}
          accountName={accountName}
          lang={clientAccount.Language}
          isBusiness={clientAccount.IsBusiness}
        />

        {clientAccount.Balances && <BalancesCard balances={clientAccount.Balances} country={Country} />}

        <TransactionLimitCard
          currencies={clientAccount.Balances?.length ? clientAccount.Balances.map(({ Currency }) => Currency) : ['CAD']}
          limit={clientAccount.TransactionLimit}
          country={Country}
        />
      </div>

      <CardTabs.Root value={tab} onValueChange={setTab}>
        <CardTabs.List
          aria-label={tCustomer('manageYourAccount', {
            defaultValue: 'Manage your account',
          })}
        >
          <CardTabs.Trigger value="details">
            {tCustomer('details', {
              defaultValue: 'Details',
            })}
          </CardTabs.Trigger>
          <CardTabs.Trigger value="activity">
            {tCustomer('activity', {
              defaultValue: 'Activity',
            })}
          </CardTabs.Trigger>
          <CardTabs.Trigger value="paymentMethods">
            {tCustomer('paymentMethods', {
              defaultValue: 'Payment Methods',
            })}
          </CardTabs.Trigger>
          {CPortalEnabled && (
            <CardTabs.Trigger value="cPortal">{tCustomer('cPortal', { defaultValue: 'cPortal' })}</CardTabs.Trigger>
          )}
          {/* <CardTabs.Trigger value="settings">Settings</CardTabs.Trigger> */}
        </CardTabs.List>

        <CardTabs.Content value="details">
          <DetailsTab
            account={{
              ...clientAccount,
              AccountID: clientAccount.ClientAccountID,
              AccountName: clientAccount.ClientName,
              AdditionalContacts:
                clientAccount.AdditionalContacts.email && clientAccount.AdditionalContacts.email.length > 0
                  ? clientAccount.AdditionalContacts.email
                  : undefined,
            }}
            onUpdate={handleUpdate}
          />
        </CardTabs.Content>

        <CardTabs.Content value="activity">
          <ActivityTab accountID={clientAccount.ClientAccountID} active={clientAccount.IsActive} />
        </CardTabs.Content>

        <CardTabs.Content value="paymentMethods" className={cn('tabContent', 'paymentMethodsTab')}>
          <PaymentMethodsTab
            groupedMethods={groupedMethods}
            onRefresh={refreshPaymentMethods}
            onSetDefaultMethod={setDefaultMethod}
            onDeleteGroupedMethod={deleteGroupedMethod}
            onDeleteMethod={deleteMethod}
            accountID={clientAccount.ClientAccountID}
            accountName={accountName}
            email={clientAccount.EmailAddress}
            active={clientAccount.IsActive}
          />
        </CardTabs.Content>

        <CardTabs.Content value="cPortal" className={cn('contentContainer')}>
          <CportalTab
            clientName={clientAccount.ClientName}
            clientEmail={clientAccount.EmailAddress}
            clientPhone={clientAccount.PhoneNumber}
            clientID={clientAccount.ClientAccountID}
            cPortalLink={cPortalLink}
            services={services}
            onUpdate={handleCportalSettingsUpdate}
          />
        </CardTabs.Content>

        <CardTabs.Content value="settings" className={cn('tabContent')}>
          <SettingsTab lang={clientAccount.Language} receiveOnly={clientAccount.ClientType === 'receive-only'} />
        </CardTabs.Content>
      </CardTabs.Root>
    </div>
  );
};

export default ClientAccountPage;
